import {
  cardId,
  createRng,
  IllegalMoveError,
  SUITS,
  standardDeck,
  type CardId,
  type GameEngine,
  type GameOptions,
  type RNG,
  type Seat,
} from "@bing/engine-core";
import {
  cardPoints,
  CARDS_PER_HAND,
  DEFAULT_MAX_HANDS,
  DEFAULT_TARGET,
  handPoints,
  isValidBid,
  legalBids,
  legalPlays,
  MAX_BID,
  pointsInCards,
  rankOf,
  scoreHand,
  SEATS,
  suitOf,
  teamOf,
  TRICK_POINTS_EACH,
  TRICKS_PER_HAND,
  trickWinner,
  WIDOW_SIZE,
} from "./rules";
import {
  SHELEM_RULES_VERSION,
  type FinishedTrick,
  type ShelemHandResult,
  type ShelemMove,
  type ShelemState,
  type ShelemView,
} from "./types";

function emptyPerSeat<T>(seats: number): T[][] {
  return Array.from({ length: seats }, () => []);
}

/** A positive integer rule override, else `fallback`. */
function positiveInt(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

/**
 * Whether `state` was made under the CURRENT rules ({@link SHELEM_RULES_VERSION}). A save from
 * the old rules (no `rulesVersion`, or 1) can be waiting for a move that no longer exists — a
 * `trump` move, a single-card discard — so the room must end it cleanly on load instead of
 * resuming it; a v2 save (the −2 × bid یاسا, until card-a7) is ended the same way, so no match
 * plays on under two scorings — with ONE deliberate exception: card-98's all-twelve scoring (the
 * حاکم's sweep with no call +330 → +165, the defenders' twelve → 165) and card-25's undoing of its
 * defenders' half (their twelve → what they took again) kept v3, so a v3 save made before either
 * resumes and plays on under the new numbers (a bump would have ended every open solo save — the
 * operator: keep them; {@link SHELEM_RULES_VERSION} has the ruling). Total over any input: `null`, a
 * number or a string is simply not current.
 *
 * …AND IN THE CURRENT SHAPE (the final review's A-I2). The OLD engine writes every state as
 * `{ ...state, … }`, so a v2 save it resumed — a game-server rolled back across the rework, then
 * redeployed — keeps its current stamp in a shape only the old rules make ({@link madeByOldRules}):
 * parked mid-کف it has no move here (a dead game), and the rest would play on under mixed rules. Only
 * shapes the old engine WRITES are refused — never a field this engine adds later — and every state
 * a v2 game reaches answers true (`test/retired-shapes.test.ts`, both halves).
 */
export function isCurrentShelemRules(state: unknown): boolean {
  if (typeof state !== "object" || state === null) return false;
  const s = state as Record<string, unknown>;
  return s["rulesVersion"] === SHELEM_RULES_VERSION && !madeByOldRules(s);
}

/** A card's suit, or null for anything that is not a card id. */
function suitOfAny(card: unknown): string | null {
  if (typeof card !== "string") return null;
  try {
    return suitOf(card);
  } catch {
    return null;
  }
}

/** The first card of the hand in play (trick 1's lead), or undefined before it. */
function firstCardOf(s: Record<string, unknown>): unknown {
  const log = s["playLog"];
  const first = Array.isArray(log) && Array.isArray(log[0]) ? log[0][0] : undefined;
  const trick = s["currentTrick"];
  const lead = first ?? (Array.isArray(trick) ? trick[0] : undefined);
  return typeof lead === "object" && lead !== null
    ? (lead as Record<string, unknown>)["card"]
    : lead;
}

/**
 * A state in a shape ONLY the old rules write (see {@link isCurrentShelemRules}) — each a v1 write
 * the fixtures in `test/fixtures/v1-touched-saves.ts` hold:
 *   · the کف laid down one card at a time — here it is ONE move off sixteen, so no discard is ever
 *     down while declaring;
 *   · trump named by a MOVE before any card, or never named — here it is exactly the suit of the
 *     hand's first card (D9), and null before it;
 *   · a hand the old engine finished — here `lastHand` always says how it was scored (`outcome`);
 *   · the old four-pass rule — here three opening passes redeal at once (D6), so three passes with
 *     no bid on the table is never a state.
 */
function madeByOldRules(s: Record<string, unknown>): boolean {
  const discards = s["discards"];
  if (s["phase"] === "declaring" && Array.isArray(discards) && discards.length > 0) return true;
  if (s["phase"] === "playing") {
    const first = firstCardOf(s);
    const trump = s["trump"] ?? null;
    if (first === undefined ? trump !== null : trump !== suitOfAny(first)) return true;
  }
  const last = s["lastHand"];
  if (
    typeof last === "object" &&
    last !== null &&
    typeof (last as Record<string, unknown>)["outcome"] !== "string"
  )
    return true;
  const passed = s["passed"];
  if (s["phase"] === "bidding" && s["highBidder"] === null && Array.isArray(passed)) {
    const seats = typeof s["seats"] === "number" ? s["seats"] : SEATS;
    if (passed.filter((p) => p === true).length >= seats - 1) return true;
  }
  return false;
}

/**
 * A VIEW THIS BRANCH's WEB CAN DRAW (the final review's I3 class). A frame from an OLD game-server —
 * master's شلم engine, before the rework — lacks what the table reads unguarded (`pointsTaken`,
 * `playedHands`, `called`; a `lastHand` with its `outcome` and breakdown): the new table threw on 13
 * of 16 frames of a real master match on one seat (`apps/web/test/shelem-old-server.test.tsx`). The
 * web shows the platform's «در حال به‌روزرسانی…» on such a frame instead; the joint push puts the web
 * first, so until the game-server follows, the window is real. Total over junk.
 */
export function isCurrentShelemView(view: unknown): boolean {
  if (typeof view !== "object" || view === null) return false;
  const v = view as Record<string, unknown>;
  const last = v["lastHand"];
  return (
    typeof v["playedHands"] === "number" &&
    Array.isArray(v["pointsTaken"]) &&
    typeof v["called"] === "boolean" &&
    (last === null ||
      (typeof last === "object" &&
        typeof (last as Record<string, unknown>)["outcome"] === "string"))
  );
}

/** The next seat round the table from `from` that is still bidding (not passed, not the standing high bidder). */
function nextBidder(
  passed: readonly boolean[],
  highBidder: Seat | null,
  from: Seat,
  seats: number,
): Seat | null {
  for (let k = 1; k <= seats; k++) {
    const seat = (from + k) % seats;
    if (!passed[seat] && seat !== highBidder) return seat;
  }
  return null;
}

/** Deal a fresh hand `handNumber` (rotating the dealer) and open the bidding. */
function dealHand(state: ShelemState, handNumber: number): ShelemState {
  const rng = createRng(state.rngState);
  const deck = rng.shuffle(standardDeck()).map(cardId);
  const hands = Array.from({ length: state.seats }, (_unused, i) =>
    deck.slice(i * CARDS_PER_HAND, (i + 1) * CARDS_PER_HAND),
  );
  const widow = deck.slice(state.seats * CARDS_PER_HAND, state.seats * CARDS_PER_HAND + WIDOW_SIZE);
  const dealer = handNumber % state.seats;
  return {
    ...state,
    phase: "bidding",
    handNumber,
    dealer,
    hands,
    widow,
    bids: Array.from({ length: state.seats }, () => null),
    passed: Array.from({ length: state.seats }, () => false),
    highBid: 0,
    highBidder: null,
    called: false,
    turn: (dealer + 1) % state.seats,
    declarer: null,
    bid: 0,
    discards: [],
    trump: null,
    currentTrick: [],
    completedTrick: [],
    leader: (dealer + 1) % state.seats,
    trickNumber: 0,
    taken: emptyPerSeat(state.seats),
    playLog: [],
    tricksWon: [0, 0],
    // a deal's own passes — the redeal (D6) sets the three that dealt it, after this
    passedOut: [],
    rngState: rng.getState(),
  };
}

/**
 * THE REDEAL's FRAME (D6; the final review B-I2): the deal the third opening «پاس» dealt, before
 * anyone has spoken in its auction — the state's or any seat's view (the game-server's scripted
 * beat and the web table read the view). The new auction's first word ends it; its `passedOut` stays
 * the deal's record. Absent `passedOut` (a row persisted before the field) is no redeal.
 */
export function isRedealFrame(
  s: Pick<ShelemView, "phase" | "highBidder" | "passed" | "passedOut">,
): boolean {
  return (
    (s.passedOut ?? []).length > 0 &&
    s.phase === "bidding" &&
    s.highBidder === null &&
    !s.passed.some(Boolean)
  );
}

/**
 * Hold a just-finished trick on the felt before the winner's next lead (client collect beat): the web
 * table holds the four cards `TRICK_HOLD_MS` (600) and sweeps them `TRICK_COLLECT_MS` (350) — its
 * `useTrickHold`, حکم's and بی‌دل's — so a bot's lead never lands under the collect. ≥ 600 + 350 +
 * 150 ms (R6), pinned by `apps/web/test/trick-hold.test.tsx`, which imports both sides.
 */
export const COMPLETED_TRICK_HOLD_MS = 1100;

/**
 * A FRESH AUCTION IS A PAUSE (audit S5): the new hand deals in on the client before the first bid
 * lands — the web's deal-in ends when its 12th card lands: 11 × 45 ms of stagger + a 450 ms flight
 * = 945 ms (the DOM test in `shelem-interim-render.test.tsx` measures the rendered value), and this
 * is that + more than the 150 ms margin every engine beat keeps over the animation it covers.
 */
export const HAND_OPEN_PAUSE_MS = 1100;

/**
 * THE کف's PICKUP IS A BEAT (R6, P1-15): the declaring phase opens as the four کف cards fly from the
 * felt to the حاکم (face-up into its hand, face-down to its seat on every other screen) and the
 * other seats read «{name} زمین را برداشت». No bot lays the کف down over it, the solo room's
 * opening stops there, and the game-server holds the حاکم's window and its `turn` frame for it
 * (`scripted-beats-shelem.ts`). ≥ the web's `KAF_PICKUP_MS` + 150 ms (`apps/web/src/game/
 * shelem-beats.ts`; `test/pacing.test.ts` imports it).
 */
export const KAF_PICKUP_BEAT_MS = 1500;

/**
 * THE TRUMP CALL IS A BEAT (R6, P1-5): the حاکم's first card of trick 1 names trump (D9) — the card
 * lands, then «حکم: ♠ — {name}» is read — so the next seat neither plays over it (a bot) nor runs
 * its window under it (a human). ≥ the web's `TRUMP_ANNOUNCE_ENDS_MS` + 150 ms (pinned the same way).
 */
export const TRUMP_ANNOUNCE_BEAT_MS = 2100;

/**
 * THE HAND'S END IS A BEAT (R6, P1-7 — P1 Task 7): the 12th trick's last card deals the next hand,
 * and that first frame carries the trick (`completedTrick`) and the finished hand (`lastHand`). The
 * table holds and collects the trick, then reads the hand-end card — the کف revealed, the contract
 * settled — so no bot speaks over it, the solo room stops there, and the game-server holds the first
 * bidder's window and its `turn` frame for it (`scripted-beats-shelem.ts`). ≥ the web's
 * `HAND_END_ENDS_MS` + 150 ms (`apps/web/src/game/shelem-beats.ts`; `test/pacing.test.ts` imports it).
 * 6600 ms since card-67 C5 (the card read 5.5 s, not 3 — «gone too fast»): 4100 until then. The
 * online table and the solo room both wait it (`postMoveDelayMs`); the match's end is no deal and no
 * beat — its end card is the web's `SHELEM_END_REVEAL_MS`, unchanged.
 */
export const HAND_END_BEAT_MS = 6600;

/**
 * THE REDEAL IS A BEAT (D6; the final review B-I2): three opening passes deal the hand again at once,
 * so the table explains it — the third «پاس» on its seat, alone, then «سه پاس؛ دست دوباره پخش می‌شه»
 * with the shuffle while the new hand deals in — and no bot opens the new auction over it; the
 * game-server holds the first bidder's window and its `turn` frame for it (`scripted-beats-shelem.ts`).
 * ≥ the web's `REDEAL_ENDS_MS` + 150 ms (`apps/web/src/game/shelem-beats.ts`; `test/pacing.test.ts`
 * imports it), and ≥ its `REDEAL_PASS_MS` + {@link HAND_OPEN_PAUSE_MS}: the new hand's own deal-in.
 */
export const REDEAL_BEAT_MS = 2200;

/** Hand the حاکم the کف (hand → 16) and enter the declaring phase. */
function startDeclaring(
  state: ShelemState,
  declarer: Seat,
  bid: number,
  called: boolean,
): ShelemState {
  const hands = state.hands.map((hand, i) => (i === declarer ? [...hand, ...state.widow] : hand));
  return {
    ...state,
    phase: "declaring",
    hands,
    widow: [],
    highBid: bid,
    highBidder: declarer,
    called,
    declarer,
    bid,
    discards: [],
    trump: null,
    turn: declarer,
  };
}

function applyBid(state: ShelemState, seat: Seat, amount: number): ShelemState {
  const bids = state.bids.map((b, i) => (i === seat ? amount : b));
  const next = nextBidder(state.passed, seat, seat, state.seats);
  // Any bid sweeps a completed trick CARRIED from the previous hand (display copy).
  const raised: ShelemState = {
    ...state,
    bids,
    highBid: amount,
    highBidder: seat,
    completedTrick: [],
  };
  // If nobody else is left to respond, this bidder takes the contract.
  return next === null ? startDeclaring(raised, seat, amount, false) : { ...raised, turn: next };
}

/**
 * The شلم call (D4). Nothing can outbid it (no سرشلم in v1), so it closes the auction at once:
 * the caller is حاکم at `MAX_BID` with `called: true`, takes the کف and leads; nobody else is
 * asked. Like a bid, it sweeps the previous hand's carried trick.
 */
function applyShelem(state: ShelemState, seat: Seat): ShelemState {
  const bids = state.bids.map((b, i) => (i === seat ? MAX_BID : b));
  return startDeclaring({ ...state, bids, completedTrick: [] }, seat, MAX_BID, true);
}

function applyPass(state: ShelemState, seat: Seat): ShelemState {
  const passed = state.passed.map((p, i) => (i === seat ? true : p));
  const passedCount = passed.filter(Boolean).length;

  // D6: the first three bidders passed and nobody bid → redeal at once to the next dealer. The
  // 4th seat is never asked, and a redeal is not a played hand (`playedHands` stays put).
  // The new deal carries the three passes (`passedOut`): no frame carried the third one.
  if (passedCount === state.seats - 1 && state.highBidder === null) {
    const passedOut = passed.flatMap((p, i) => (p ? [i] : []));
    return { ...dealHand({ ...state, passed }, state.handNumber + 1), passedOut };
  }

  const next = nextBidder(passed, state.highBidder, seat, state.seats);
  if (next === null) {
    // Only the high bidder remains un-passed → they are the حاکم.
    if (state.highBidder === null) throw new Error("unreachable: no bidder left but no high bid");
    return startDeclaring({ ...state, passed }, state.highBidder, state.highBid, false);
  }
  return { ...state, passed, turn: next, completedTrick: [] };
}

/** The ONE discard: lay the 4 cards down and go straight to playing. Trump stays unnamed until
 *  the حاکم's first card (D9), and the حاکم leads trick 1. */
function applyDiscard(state: ShelemState, seat: Seat, cards: readonly CardId[]): ShelemState {
  const drop = new Set(cards);
  const hands = state.hands.map((hand, i) =>
    i === seat ? hand.filter((c) => !drop.has(c)) : hand,
  );
  return {
    ...state,
    hands,
    discards: [...cards],
    phase: "playing",
    trump: null,
    leader: seat,
    turn: seat,
    currentTrick: [],
    trickNumber: 0,
  };
}

/**
 * The legal-move SAMPLE for the discard: the 4 lowest-value cards (fewest card points, then the
 * lowest ranks; suits break ties). `legalMoves` returns only this one discard — enumerating every
 * C(16,4) = 1820 choice would make each call allocate a large list. `isLegal` accepts ANY 4
 * distinct cards the حاکم holds, so a bot or a client simply builds its own choice.
 */
function sampleDiscard(hand: readonly CardId[]): CardId[] {
  const key = (c: CardId): number =>
    cardPoints(c) * 1000 + rankOf(c) * 10 + SUITS.indexOf(suitOf(c));
  return [...hand].sort((a, b) => key(a) - key(b)).slice(0, WIDOW_SIZE);
}

/** Whether `cards` is a legal discard for the حاکم `seat`: exactly 4 distinct cards it holds. */
function isLegalDiscard(state: ShelemState, seat: Seat, cards: unknown): boolean {
  if (state.phase !== "declaring" || seat !== state.declarer || state.discards.length > 0) {
    return false;
  }
  const hand = state.hands[seat]!;
  if (hand.length !== CARDS_PER_HAND + WIDOW_SIZE) return false;
  if (!Array.isArray(cards) || cards.length !== WIDOW_SIZE) return false;
  if (new Set(cards).size !== WIDOW_SIZE) return false;
  return cards.every((c) => typeof c === "string" && hand.includes(c));
}

/** Who wins once a match is over (D14): the higher total; an exact tie goes to the team that
 *  held the last hand's contract. Only a hand-built state can tie with no hand played yet —
 *  team 0 then, so the answer is always exactly one team. */
function winningTeam(state: ShelemState): number {
  const [t0, t1] = state.teamScores as [number, number];
  if (t0 !== t1) return t0 > t1 ? 0 : 1;
  return state.lastHand?.declaringTeam ?? 0;
}

/** Tally the just-finished hand, update match scores, and start the next hand or end the match. */
function finishHand(state: ShelemState): ShelemState {
  const declarer = state.declarer!;
  const trump = state.trump!;
  const declaringTeam = teamOf(declarer);
  const points = handPoints(state.taken, state.tricksWon, state.discards, declaringTeam);
  const sweep = state.tricksWon[declaringTeam] === TRICKS_PER_HAND;
  const { teamDelta, made, outcome } = scoreHand(
    points.declPoints,
    points.defPoints,
    state.bid,
    declaringTeam,
    sweep,
    state.called,
  );

  const teamScores = [state.teamScores[0]! + teamDelta[0], state.teamScores[1]! + teamDelta[1]];
  const lastHand: ShelemHandResult = {
    declarer,
    declaringTeam,
    bid: state.bid,
    called: state.called,
    trump,
    ...points,
    tricksWon: [...state.tricksWon],
    discards: [...state.discards],
    made,
    sweep,
    outcome,
    teamDelta,
  };
  const playedHands = state.playedHands + 1;

  const reachedTarget = teamScores.some((s) => s >= state.targetScore);
  if (reachedTarget || playedHands >= state.maxHands) {
    return { ...state, phase: "gameOver", teamScores, lastHand, playedHands };
  }
  // CARRY the hand-ending 12th trick into the fresh hand as a DISPLAY copy (its cards already
  // live in `taken` — conservation-exempt) so the client can hold it on the felt while the next
  // hand's bidding opens. Swept by the first auction move.
  return {
    ...dealHand({ ...state, teamScores, lastHand, playedHands }, state.handNumber + 1),
    completedTrick: state.completedTrick ?? [],
  };
}

function applyPlay(state: ShelemState, seat: Seat, card: CardId): ShelemState {
  const hands = state.hands.map((hand, i) => (i === seat ? hand.filter((c) => c !== card) : hand));
  // The lead of a fresh trick sweeps the completed trick still held on screen.
  const leadingNew = state.currentTrick.length === 0;
  const currentTrick = [...state.currentTrick, { seat, card }];
  // D9: the حاکم's first card of trick 1 names trump, fixed for the rest of the hand.
  const trump = state.trump ?? suitOf(card);

  if (currentTrick.length < state.seats) {
    return {
      ...state,
      hands,
      trump,
      currentTrick,
      completedTrick: leadingNew ? [] : (state.completedTrick ?? []),
      turn: (seat + 1) % state.seats,
    };
  }

  // Trick complete — award it and lead the next. The four plays are KEPT in
  // `completedTrick` (currentTrick clears) so the client can show the finished
  // trick — winning 4th card and all — until the next lead (mirrors Hokm/Hearts).
  const winner = trickWinner(currentTrick, trump);
  const trickCards = currentTrick.map((play) => play.card);
  const taken = state.taken.map((cards, i) => (i === winner ? [...cards, ...trickCards] : cards));
  const tricksWon = [...state.tricksWon];
  tricksWon[teamOf(winner)] = tricksWon[teamOf(winner)]! + 1;
  const next: ShelemState = {
    ...state,
    hands,
    trump,
    currentTrick: [],
    completedTrick: currentTrick,
    taken,
    // Append the just-finished trick to the public play-history for this hand.
    playLog: [...state.playLog, currentTrick],
    // …and name it as the match's newest FINISHED trick, with its winner (card-a5 item 9, R5): unlike
    // the two above, nothing but the next finished trick replaces it — not the next deal or a redeal,
    // not the auction's first word that clears the carried copy — so a client that missed this very
    // frame can still draw it.
    lastTrick: { plays: currentTrick, winner },
    tricksWon,
    trickNumber: state.trickNumber + 1,
    leader: winner,
    turn: winner,
  };

  return hands.every((hand) => hand.length === 0) ? finishHand(next) : next;
}

/** A view's copy of {@link ShelemState.lastTrick} — never the state's own arrays; `null` when none. */
function projectLastTrick(last: FinishedTrick | null | undefined): FinishedTrick | null {
  return last ? { plays: last.plays.map((play) => ({ ...play })), winner: last.winner } : null;
}

/** Card points + 5 per trick each team has won so far this hand — never the کف. */
function pointsTaken(state: ShelemState): number[] {
  const out = [0, 0];
  state.taken.forEach((cards, seat) => {
    out[teamOf(seat)] = out[teamOf(seat)]! + pointsInCards(cards);
  });
  return out.map((pts, team) => pts + state.tricksWon[team]! * TRICK_POINTS_EACH);
}

export const shelemEngine: GameEngine<ShelemState, ShelemMove, ShelemView> = {
  gameId: "shelem",

  /**
   * R2 (card-91): a save made under the OLD rules (no `rulesVersion`, or 1) can be parked on a move
   * that no longer exists — a `trump` move, a single-card discard — so the solo room retires it on
   * load (`SAVED_GAME_RETIRED_MESSAGE`, the row deleted, nothing recorded) instead of resuming it.
   * So does a current save the old engine resumed and reshaped (A-I2: a rollback window), and a v2
   * save, scored under the −2 × bid یاسا (card-a7).
   */
  canResume: isCurrentShelemRules,

  createInitialState(opts: GameOptions, rng: RNG): ShelemState {
    const seats = SEATS;
    const seed: ShelemState = {
      rulesVersion: SHELEM_RULES_VERSION,
      seats,
      phase: "bidding",
      handNumber: 0,
      playedHands: 0,
      dealer: 0,
      hands: emptyPerSeat(seats),
      widow: [],
      bids: Array.from({ length: seats }, () => null),
      passed: Array.from({ length: seats }, () => false),
      highBid: 0,
      highBidder: null,
      called: false,
      turn: 0,
      declarer: null,
      bid: 0,
      discards: [],
      trump: null,
      currentTrick: [],
      leader: 0,
      trickNumber: 0,
      taken: emptyPerSeat(seats),
      playLog: [],
      tricksWon: [0, 0],
      teamScores: [0, 0],
      lastHand: null,
      // D13: 1200 unless the room sends its own (any positive integer — the rooms send 660/1200).
      targetScore: positiveInt(opts.rules?.["targetScore"], DEFAULT_TARGET),
      maxHands: positiveInt(opts.rules?.["maxHands"], DEFAULT_MAX_HANDS),
      lastTrick: null, // no trick has finished yet — every deal after this one inherits the record
      rngState: rng.getState(),
    };
    return dealHand(seed, 0);
  },

  legalMoves(state: ShelemState, seat: Seat): ShelemMove[] {
    if (state.phase === "bidding") {
      if (seat !== state.turn || state.passed[seat]) return [];
      const moves: ShelemMove[] = [{ type: "pass" }];
      for (const amount of legalBids(state.highBid)) moves.push({ type: "bid", amount });
      moves.push({ type: "shelem" });
      return moves;
    }
    if (state.phase === "declaring") {
      if (seat !== state.declarer) return [];
      // A SAMPLE, not every legal discard — see `sampleDiscard`.
      const sample = sampleDiscard(state.hands[seat]!);
      return isLegalDiscard(state, seat, sample) ? [{ type: "discard", cards: sample }] : [];
    }
    if (state.phase === "playing") {
      if (seat !== state.turn) return [];
      return legalPlays(state.hands[seat]!, state.currentTrick).map((card) => ({
        type: "play",
        card,
      }));
    }
    return [];
  },

  isLegal(state: ShelemState, seat: Seat, move: ShelemMove): boolean {
    switch (move.type) {
      case "pass":
        return state.phase === "bidding" && seat === state.turn && !state.passed[seat];
      case "bid":
        return (
          state.phase === "bidding" &&
          seat === state.turn &&
          !state.passed[seat] &&
          isValidBid(move.amount, state.highBid)
        );
      case "shelem":
        // Any open auction turn: a call ends the auction, so bidding means it is still open.
        return state.phase === "bidding" && seat === state.turn && !state.passed[seat];
      case "discard":
        return isLegalDiscard(state, seat, move.cards);
      case "play":
        return (
          state.phase === "playing" &&
          seat === state.turn &&
          legalPlays(state.hands[seat]!, state.currentTrick).includes(move.card)
        );
      default:
        // An unknown move type (an old client's `trump`, a single-card discard, junk): never legal.
        return false;
    }
  },

  applyMove(state: ShelemState, seat: Seat, move: ShelemMove): ShelemState {
    if (!shelemEngine.isLegal(state, seat, move)) throw new IllegalMoveError(seat, move);
    switch (move.type) {
      case "bid":
        return applyBid(state, seat, move.amount);
      case "shelem":
        return applyShelem(state, seat);
      case "pass":
        return applyPass(state, seat);
      case "discard":
        return applyDiscard(state, seat, move.cards);
      case "play":
        return applyPlay(state, seat, move.card);
    }
  },

  isTerminal(state: ShelemState): boolean {
    return state.phase === "gameOver";
  },

  /**
   * The match result. Never a draw and never four winners (D14): `winners` is exactly the winning
   * team's two seats — the higher total, or on an exact tie the last contract team — and `tied`
   * is never set. `detail.handsPlayed` counts PLAYED hands only (redeals excluded).
   */
  score(state: ShelemState) {
    const teamScores = [...state.teamScores];
    const team = winningTeam(state);
    const winners: Seat[] = [];
    for (let seat = 0; seat < state.seats; seat++) {
      if (teamOf(seat) === team) winners.push(seat);
    }
    const seatScores = Array.from(
      { length: state.seats },
      (_unused, seat) => teamScores[teamOf(seat)]!,
    );
    return {
      seatScores,
      teamScores,
      winners,
      detail: {
        lastHand: state.lastHand,
        handsPlayed: state.playedHands,
        targetScore: state.targetScore,
      },
    };
  },

  /**
   * Pacing hint for the realtime rooms (solo + online read it identically):
   *  - hold a just-completed trick on the felt before the winner's next lead — the client shows
   *    the finished trick, 4th card and all, during this beat. KEEP it above the web table's hold
   *    so the felt is clear when play resumes;
   *  - hold a FRESH AUCTION ({@link HAND_OPEN_PAUSE_MS}) — a new deal or a redeal, before anyone has
   *    bid or passed — so the hand deals in before a bot's first bid lands. It is also what makes
   *    the solo room's opening stop at the deal (its fast-forward stops at any pause), so a solo
   *    game's first frame is the deal itself, never bids played before the client connected (S5);
   *  - hold THE کف's PICKUP ({@link KAF_PICKUP_BEAT_MS}) — the declaring phase, the four cards in
   *    flight to the حاکم — so a bot حاکم never lays the کف down while it flies;
   *  - hold THE TRUMP CALL ({@link TRUMP_ANNOUNCE_BEAT_MS}) — trick 1's first card, the one that
   *    named trump — so the next card never lands over «حکم: ♠ — {name}»;
   *  - hold THE HAND'S END ({@link HAND_END_BEAT_MS}) — the deal after a played hand, its first
   *    frame: the 12th trick carried, collected, then the hand-end card read before the next auction's
   *    first word (any bid or pass sweeps the carried trick, so the beat is that frame's alone);
   *  - hold THE REDEAL ({@link REDEAL_BEAT_MS}) — the deal three opening passes dealt, its first frame
   *    ({@link isRedealFrame}): the third «پاس», then the notice over the new hand's deal-in.
   * The deal after a played hand carries its 12th trick AND opens an auction: the longest beat wins.
   */
  postMoveDelayMs(state: ShelemState): number | undefined {
    const holding = (state.completedTrick ?? []).length > 0 ? COMPLETED_TRICK_HOLD_MS : 0;
    const opening =
      state.phase === "bidding" && state.highBidder === null && !state.passed.some(Boolean)
        ? HAND_OPEN_PAUSE_MS
        : 0;
    const pickup = state.phase === "declaring" ? KAF_PICKUP_BEAT_MS : 0;
    const announce =
      state.phase === "playing" && state.trickNumber === 0 && state.currentTrick.length === 1
        ? TRUMP_ANNOUNCE_BEAT_MS
        : 0;
    const handEnd =
      state.phase === "bidding" &&
      (state.completedTrick ?? []).length > 0 &&
      state.lastHand !== null
        ? HAND_END_BEAT_MS
        : 0;
    const redeal = isRedealFrame(state) ? REDEAL_BEAT_MS : 0;
    const beat = Math.max(holding, opening, pickup, announce, handEnd, redeal);
    return beat > 0 ? beat : undefined;
  },

  viewFor(state: ShelemState, seat: Seat): ShelemView {
    return {
      seats: state.seats,
      phase: state.phase,
      you: seat,
      hand: [...state.hands[seat]!],
      handCounts: state.hands.map((hand) => hand.length),
      widowCount: state.widow.length,
      dealer: state.dealer,
      bids: [...state.bids],
      passed: [...state.passed],
      highBid: state.highBid,
      highBidder: state.highBidder,
      called: state.called,
      turn: state.turn,
      declarer: state.declarer,
      bid: state.bid,
      trump: state.trump,
      discardCount: state.discards.length,
      currentTrick: state.currentTrick.map((play) => ({ ...play })),
      // `?? []` — legacy persisted rows (saved before the field existed) resume safely.
      completedTrick: (state.completedTrick ?? []).map((play) => ({ ...play })),
      playLog: state.playLog.map((trick) => trick.map((play) => ({ ...play }))),
      leader: state.leader,
      trickNumber: state.trickNumber,
      tricksWon: [...state.tricksWon],
      pointsTaken: pointsTaken(state),
      teamScores: [...state.teamScores],
      // A finished hand only: its discards are public now (the current hand's never are).
      lastHand: state.lastHand,
      targetScore: state.targetScore,
      playedHands: state.playedHands,
      // The match's newest finished trick (card-a5 item 9, R5) — public, a copy; `?? null` for a row
      // persisted before the field existed.
      lastTrick: projectLastTrick(state.lastTrick),
      // the three passes that redealt this deal (D6) — public; `?? []` for a row persisted before
      passedOut: [...(state.passedOut ?? [])],
    };
  },
};
